/**
 * Gmail API Service
 * Handles Gmail API integration for creating draft emails
 */

// Gmail API configuration
const SCOPES = 'https://www.googleapis.com/auth/gmail.compose';
const DISCOVERY_DOC = 'https://www.googleapis.com/discovery/v1/apis/gmail/v1/rest';

class GmailService {
  constructor() {
    this.tokenClient = null;
    this.gapiInited = false;
    this.gisInited = false;
  }

  /**
   * Initialize Google API client
   */
  async initializeGoogleAPI(clientId, apiKey) {
    try {
      // Load gapi if not already loaded
      if (!window.gapi) {
        await this.loadGapiScript();
      }

      // Initialize gapi client
      await new Promise((resolve, reject) => {
        window.gapi.load('client', async () => {
          try {
            await window.gapi.client.init({
              apiKey: apiKey,
              discoveryDocs: [DISCOVERY_DOC],
            });
            this.gapiInited = true;
            console.log('✅ Gmail API initialized');
            resolve();
          } catch (error) {
            console.error('❌ Error initializing Gmail API:', error);
            reject(error);
          }
        });
      });

      // Initialize Google Identity Services
      if (!window.google) {
        await this.loadGisScript();
      }

      this.tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: clientId,
        scope: SCOPES,
        callback: '', // Will be set when needed
      });
      this.gisInited = true;
      console.log('✅ Google Identity Services initialized');

      return true;
    } catch (error) {
      console.error('❌ Failed to initialize Google APIs:', error);
      throw error;
    }
  }

  /**
   * Load Google API Platform Library script
   */
  loadGapiScript() {
    return new Promise((resolve, reject) => {
      // If script already exists and gapi is loaded, resolve immediately
      if (window.gapi) {
        resolve();
        return;
      }

      // If script tag exists but gapi not loaded yet, wait for it
      if (document.getElementById('gapi-script')) {
        const checkGapi = setInterval(() => {
          if (window.gapi) {
            clearInterval(checkGapi);
            resolve();
          }
        }, 100);
        setTimeout(() => {
          clearInterval(checkGapi);
          reject(new Error('Timeout waiting for gapi to load'));
        }, 10000);
        return;
      }

      // Load the script
      const script = document.createElement('script');
      script.id = 'gapi-script';
      script.src = 'https://apis.google.com/js/api.js';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        // Wait for gapi to be available
        const checkGapi = setInterval(() => {
          if (window.gapi) {
            clearInterval(checkGapi);
            resolve();
          }
        }, 100);
        setTimeout(() => {
          clearInterval(checkGapi);
          reject(new Error('gapi not available after script load'));
        }, 5000);
      };
      script.onerror = () => reject(new Error('Failed to load gapi script'));
      document.head.appendChild(script);
    });
  }

  /**
   * Load Google Identity Services script
   */
  loadGisScript() {
    return new Promise((resolve, reject) => {
      // If script already exists and google.accounts is loaded, resolve immediately
      if (window.google && window.google.accounts) {
        resolve();
        return;
      }

      // If script tag exists but not loaded yet, wait for it
      if (document.getElementById('gis-script')) {
        const checkGis = setInterval(() => {
          if (window.google && window.google.accounts) {
            clearInterval(checkGis);
            resolve();
          }
        }, 100);
        setTimeout(() => {
          clearInterval(checkGis);
          reject(new Error('Timeout waiting for GIS to load'));
        }, 10000);
        return;
      }

      // Load the script
      const script = document.createElement('script');
      script.id = 'gis-script';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        // Wait for google.accounts to be available
        const checkGis = setInterval(() => {
          if (window.google && window.google.accounts) {
            clearInterval(checkGis);
            resolve();
          }
        }, 100);
        setTimeout(() => {
          clearInterval(checkGis);
          reject(new Error('GIS not available after script load'));
        }, 5000);
      };
      script.onerror = () => reject(new Error('Failed to load GIS script'));
      document.head.appendChild(script);
    });
  }

  /**
   * Request authorization from user
   */
  async authorize() {
    return new Promise((resolve, reject) => {
      if (!this.tokenClient) {
        reject(new Error('Token client not initialized'));
        return;
      }

      this.tokenClient.callback = async (response) => {
        if (response.error) {
          reject(response);
          return;
        }
        resolve(response);
      };

      // Check if user already has a valid token
      if (window.gapi.client.getToken() === null) {
        // Prompt the user to select a Google Account and ask for consent
        this.tokenClient.requestAccessToken({ prompt: 'consent' });
      } else {
        // Skip display of account chooser and consent dialog for an existing session
        this.tokenClient.requestAccessToken({ prompt: '' });
      }
    });
  }

  /**
   * Create a Gmail draft with review request
   * @param {string} recipientEmail - Client's email address
   * @param {string} clientName - Client's name for personalization
   * @param {string} reviewLink - Google review link
   * @returns {Promise<Object>} - Draft creation result
   */
  async createReviewRequestDraft(recipientEmail, clientName, reviewLink) {
    try {
      if (!this.gapiInited || !this.gisInited) {
        throw new Error('Gmail API not initialized');
      }

      // Authorize if needed
      await this.authorize();

      // Create email content
      const emailContent = this.generateReviewEmailContent(recipientEmail, clientName, reviewLink);

      // Create draft using Gmail API
      const response = await window.gapi.client.gmail.users.drafts.create({
        userId: 'me',
        resource: {
          message: {
            raw: this.base64EncodeEmail(emailContent)
          }
        }
      });

      console.log('✅ Gmail draft created:', response);
      return {
        success: true,
        draftId: response.result.id,
        message: 'Draft created successfully in Gmail'
      };
    } catch (error) {
      console.error('❌ Error creating Gmail draft:', error);
      return {
        success: false,
        error: error.message || 'Failed to create draft'
      };
    }
  }

  /**
   * Generate email content for review request
   */
  generateReviewEmailContent(recipientEmail, clientName, reviewLink) {
    const subject = 'Thank you for trusting us with your estate planning';
    const body = `Dear ${clientName},

Thank you for choosing our firm to handle your estate planning needs. We hope you are satisfied with the services we provided.

Your feedback is incredibly valuable to us and helps others in our community make informed decisions about their estate planning needs. If you had a positive experience working with us, we would be grateful if you could take a moment to share your thoughts by leaving a review.

You can leave a review by clicking the link below:
${reviewLink}

Your review will help us continue to improve our services and assist future clients in making their estate planning decisions.

Thank you again for your trust and confidence in our firm.

Best regards,
Rozsa Gyene Law Office

---
This email was generated automatically from our case management system.`;

    return `To: ${recipientEmail}
Subject: ${subject}
Content-Type: text/plain; charset=utf-8

${body}`;
  }

  /**
   * Base64 encode email content for Gmail API
   */
  base64EncodeEmail(email) {
    return btoa(unescape(encodeURIComponent(email)))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  /**
   * Sign out the user
   */
  signOut() {
    const token = window.gapi.client.getToken();
    if (token !== null) {
      window.google.accounts.oauth2.revoke(token.access_token);
      window.gapi.client.setToken('');
    }
  }
}

// Export singleton instance
const gmailService = new GmailService();
export default gmailService;
